import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './GameScreen.css';
import { fetchNewChallenge, submitPosition, finalizeGameSession, fetchMapData, fetchUserSettings, fetchSounds, clearApiCache } from '../services/apiService';

const GameScreen = ({ gameState, updateGameState, onGameEnd, user, globalMuted, setGlobalMuted, globalVolume, setGlobalVolume }) => {
  const { t } = useTranslation();

  // State variables for game management
  const [loading, setLoading] = useState(true);
  const [selectedMap, setSelectedMap] = useState('');
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedPositionRelative, setSelectedPositionRelative] = useState(null);
  const [roundResult, setRoundResult] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(60);
  const [mapData, setMapData] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [tempSelectedMap, setTempSelectedMap] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorCallback, setErrorCallback] = useState(null);
  const [showFinalScoreModal, setShowFinalScoreModal] = useState(false);
  const [timeExpired, setTimeExpired] = useState(false);
  // We only need the setter for timerAlertPlayed
  const [, setTimerAlertPlayed] = useState(false);

  // State variables for zoom functionality
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Reset zoom and pan
  const resetZoomAndPan = useCallback(() => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  }, []);

  // Sound-related state variables
  const [selectedTimerAlert, setSelectedTimerAlert] = useState(null);
  const [disableSoundAlerts, setDisableSoundAlerts] = useState(false);
  const [sounds, setSounds] = useState([]);

  // Flags to track if data has been loaded
  const [userSettingsLoaded, setUserSettingsLoaded] = useState(false);
  const [soundsLoaded, setSoundsLoaded] = useState(false);
  const [challengeLoaded, setChallengeLoaded] = useState(false);
  const [mapDataLoaded, setMapDataLoaded] = useState(false);

  // Wrapper for onGameEnd
  const handleGameEnd = useCallback(() => {
    // Reset all loading flags to ensure fresh data on next game start
    setChallengeLoaded(false);
    setUserSettingsLoaded(false);
    setSoundsLoaded(false);
    setMapDataLoaded(false);

    // Clear API cache to ensure fresh data on next game start
    clearApiCache();

    // Call the original onGameEnd function
    onGameEnd();
  }, [onGameEnd]);

  // Refs for DOM elements
  const timerRef = useRef(null);
  // mapContainerRef is not used in this component
  const modalMapContainerRef = useRef(null);
  const selectedMapContainerRef = useRef(null);
  const modalMapImageRef = useRef(null);
  const selectedMapImageRef = useRef(null);
  const modalMapOverlayRef = useRef(null);
  const selectedMapOverlayRef = useRef(null);

  // Convert relative position (0-1) to absolute position (pixels)
  const relativeToAbsolutePosition = useCallback((relativePos, containerRef, imageRef) => {
    if (!relativePos || !containerRef.current || !imageRef?.current) return null;

    const containerRect = containerRef.current.getBoundingClientRect();

    // Calculate the actual image dimensions within the container
    // When using object-fit: contain, the image maintains its aspect ratio
    // and may have empty space on the sides or top/bottom
    const imageAspectRatio = mapData[selectedMap]?.width / mapData[selectedMap]?.height || 1;
    const containerAspectRatio = containerRect.width / containerRect.height;

    let actualImageWidth, actualImageHeight;
    let offsetX = 0, offsetY = 0;

    if (imageAspectRatio > containerAspectRatio) {
      // Image is wider than container (empty space on top and bottom)
      actualImageWidth = containerRect.width;
      actualImageHeight = containerRect.width / imageAspectRatio;
      offsetY = (containerRect.height - actualImageHeight) / 2;
    } else {
      // Image is taller than container (empty space on sides)
      actualImageHeight = containerRect.height;
      actualImageWidth = containerRect.height * imageAspectRatio;
      offsetX = (containerRect.width - actualImageWidth) / 2;
    }

    // Ensure relativePos is within the range [0, 1] to keep the dot within the image boundaries
    const clampedRelativePos = {
      x: Math.max(0, Math.min(1, relativePos.x)),
      y: Math.max(0, Math.min(1, relativePos.y))
    };

    // Calculate position relative to the actual image dimensions
    return {
      x: offsetX + (clampedRelativePos.x * actualImageWidth),
      y: offsetY + (clampedRelativePos.y * actualImageHeight)
    };
  }, [mapData, selectedMap]);

  // Convert absolute position (pixels) to relative position (0-1)
  // This function is defined but not currently used in the component
  // Keeping it commented out for potential future use
  /*
  const absoluteToRelativePosition = useCallback((absolutePos, containerRef, imageRef) => {
    if (!absolutePos || !containerRef.current || !imageRef?.current) return null;

    const containerRect = containerRef.current.getBoundingClientRect();

    // Calculate the actual image dimensions within the container
    const imageAspectRatio = mapData[selectedMap]?.width / mapData[selectedMap]?.height || 1;
    const containerAspectRatio = containerRect.width / containerRect.height;

    let actualImageWidth, actualImageHeight;
    let offsetX = 0, offsetY = 0;

    if (imageAspectRatio > containerAspectRatio) {
      // Image is wider than container (empty space on top and bottom)
      actualImageWidth = containerRect.width;
      actualImageHeight = containerRect.width / imageAspectRatio;
      offsetY = (containerRect.height - actualImageHeight) / 2;
    } else {
      // Image is taller than container (empty space on sides)
      actualImageHeight = containerRect.height;
      actualImageWidth = containerRect.height * imageAspectRatio;
      offsetX = (containerRect.width - actualImageWidth) / 2;
    }

    // Calculate position relative to the actual image dimensions
    const relativePos = {
      x: (absolutePos.x - offsetX) / actualImageWidth,
      y: (absolutePos.y - offsetY) / actualImageHeight
    };

    // Ensure the relative position is within the range [0, 1]
    return {
      x: Math.max(0, Math.min(1, relativePos.x)),
      y: Math.max(0, Math.min(1, relativePos.y))
    };
  }, [mapData, selectedMap]);
  */


  // Function to recalculate positions based on current state
  const recalculatePositions = useCallback(() => {
    // Only recalculate if we're showing results (map with points)
    if (showResults && selectedMapContainerRef.current && selectedMapImageRef.current) {
      // Recalculate selected position
      if (selectedPositionRelative) {
        const newAbsolutePos = relativeToAbsolutePosition(selectedPositionRelative, selectedMapContainerRef, selectedMapImageRef);
        if (newAbsolutePos && 
            (!selectedPosition || 
             selectedPosition.x !== newAbsolutePos.x || 
             selectedPosition.y !== newAbsolutePos.y)) {
          setSelectedPosition(newAbsolutePos);
        }
      }

    }
    // Handle position in modal
    else if (showMapModal && selectedPositionRelative && modalMapContainerRef.current && modalMapImageRef.current) {
      const newAbsolutePos = relativeToAbsolutePosition(selectedPositionRelative, modalMapContainerRef, modalMapImageRef);
      if (newAbsolutePos && 
          (!selectedPosition || 
           selectedPosition.x !== newAbsolutePos.x || 
           selectedPosition.y !== newAbsolutePos.y)) {
        setSelectedPosition(newAbsolutePos);
      }
    }
  }, [
    showResults, 
    selectedPositionRelative, 
    relativeToAbsolutePosition, 
    showMapModal,
    selectedPosition
  ]);

  // Load map data when component mounts
  useEffect(() => {
    const loadMapData = async () => {
      if (mapDataLoaded) return;

      try {
        const maps = await fetchMapData();
        setMapData(maps);
        setMapDataLoaded(true);
      } catch (error) {
        console.error('Error loading map data:', error);
      }
    };

    loadMapData();
  }, [mapDataLoaded]);

  // Load user settings when component mounts
  useEffect(() => {
    const loadUserSettings = async () => {
      if (userSettingsLoaded) return;

      try {
        // Fetch user settings
        const settings = await fetchUserSettings();
        setSelectedTimerAlert(settings.selected_timer_alert || null);
        setDisableSoundAlerts(settings.disable_sound_alerts || false);
        setUserSettingsLoaded(true);
      } catch (error) {
        console.error('Error loading user settings:', error);
      }
    };

    loadUserSettings();
  }, [userSettingsLoaded]);

  // Load sounds when component mounts
  useEffect(() => {
    const loadSounds = async () => {
      if (soundsLoaded) return;

      try {
        // Fetch available sounds
        const soundsData = await fetchSounds();
        setSounds(soundsData);
        setSoundsLoaded(true);
      } catch (error) {
        console.error('Error loading sounds:', error);
      }
    };

    loadSounds();
  }, [soundsLoaded]);

  // Load challenge when component mounts or round changes
  useEffect(() => {
    const loadChallenge = async () => {
      // If we already have a challenge for this round, don't load another one
      if (challengeLoaded && gameState.challengeId) return;

      setLoading(true);
      try {
        const challenge = await fetchNewChallenge(
          gameState.difficulty, 
          gameState.sessionId,
          gameState.gamemode
        );

        updateGameState({
          challengeId: challenge.id,
          challengeImage: challenge.image,
          sessionId: challenge.sessionId
        });

        setChallengeLoaded(true);
        setLoading(false);
      } catch (error) {
        console.error('Error loading challenge:', error);

        // Check if this is a daily limit error
        if (error.response && error.response.status === 403 && 
            error.response.data && error.response.data.error === 'daily_limit_reached') {
          // Show daily limit reached message
          showError(t('game.dailyLimitReached', 'You have reached the daily limit of games. Please try again tomorrow.'), onGameEnd);
        }

        setLoading(false);
      }
    };

    // Only load challenge if we have a valid round and we're not already showing a challenge
    if (gameState.currentRound > 0 && !gameState.challengeId) {
      loadChallenge();
    }
  }, [gameState.currentRound, gameState.difficulty, gameState.sessionId, gameState.gamemode, updateGameState, gameState.challengeId, onGameEnd, t, challengeLoaded]);

  // Handle window resize to recalculate positions with debounce
  useEffect(() => {
    // Debounce function to prevent too many recalculations during resize
    let resizeTimer;
    const handleResize = () => {
      // Only recalculate if we're showing results or the map modal
      if (showResults || showMapModal) {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          recalculatePositions();
        }, 100); // 100ms debounce
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, [recalculatePositions, showResults, showMapModal]);

  // Recalculate positions when showResults or showMapModal changes
  useEffect(() => {
    // Only recalculate if we're showing results or the map modal
    // This prevents unnecessary calculations when neither is active
    if (showResults || showMapModal) {
      // Use setTimeout to ensure the DOM has updated before recalculating
      const timer = setTimeout(() => {
        recalculatePositions();
      }, 0);

      return () => clearTimeout(timer);
    } else {
      // Reset zoom and pan when not showing results or map modal
      resetZoomAndPan();
    }
  }, [showResults, showMapModal, recalculatePositions, resetZoomAndPan]);


  // Function to handle time expiration - defined outside useEffect to avoid circular dependency
  const handleTimeExpiration = useCallback(() => {
    // Clear the interval
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Close map modal if it's open
    if (showMapModal) {
      setShowMapModal(false);
    }

    // We'll set a flag to indicate time expiration
    // The actual submission will happen in the handleSubmit function
    setTimeExpired(true);

    // Submit with default values
    const defaultMap = Object.keys(mapData)[0] || '';
    const defaultPosition = { x: 0.5, y: 0.5 }; // Center in 0-1 range

    // Update state for UI consistency
    setSelectedMap(defaultMap);
    setSelectedPositionRelative(defaultPosition);

    // Trigger submission with the default map
    if (defaultMap) {
      // Get map dimensions from mapData
      const mapWidth = mapData[defaultMap]?.width || 100;
      const mapHeight = mapData[defaultMap]?.height || 100;

      // Convert relative coordinates to absolute pixel coordinates
      const absolutePosition = { 
        x: defaultPosition.x * mapWidth, 
        y: defaultPosition.y * mapHeight 
      };

      // Submit the position directly to the API
      submitPosition(
        gameState.challengeId,
        defaultMap,
        absolutePosition,
        gameState.sessionId
      ).then(result => {
        // Create a result object with 0 points
        const timeExpiredResult = {
          score: 0,
          distance: -1, // -1 to hide the line and actual position
          timeExpired: true
        };

        // Update scores in game state with 0 points
        const updatedScores = [...gameState.scores, 0];
        updateGameState({ scores: updatedScores });

        // Set round result and show results
        setRoundResult(timeExpiredResult);
        setShowResults(true);
      }).catch(error => {
        console.error('Error submitting position after time expired:', error);
        showError(t('game.errorSubmittingPosition', 'Error submitting position. Please try again.'));
      });
    }

    return 0;
  }, [
    timerRef, 
    showMapModal, 
    mapData, 
    gameState.challengeId, 
    gameState.sessionId, 
    gameState.scores, 
    t, 
    updateGameState
  ]);

  // Set up timer
  useEffect(() => {
    // Don't start timer if loading, showing results, or no map data available
    if (loading || showResults || Object.keys(mapData).length === 0) return;

    // Reset timer alert played state when timer starts
    setTimerAlertPlayed(false);

    // Function to play timer alert sound
    const playTimerAlert = () => {
      // If sound alerts are disabled or session is muted, don't play any sound
      if (disableSoundAlerts || globalMuted) {
        setTimerAlertPlayed(true);
        return;
      }

      try {
        // Use selected timer alert sound if available, otherwise use default
        if (selectedTimerAlert) {
          const sound = sounds.find(s => s.code === selectedTimerAlert);
          if (sound) {
            const audio = new Audio(sound.path);
            audio.volume = globalVolume / 100; // Set volume based on globalVolume (0-100 to 0-1)
            audio.play().catch(error => {
              console.error('Error playing timer alert sound:', error);
            });
            setTimerAlertPlayed(true);
            return;
          }
        }

        // Fallback to default sound if no selection or selected sound not found
        const timerAlertSound = new Audio('/sounds/timer_alert.mp3');
        timerAlertSound.volume = globalVolume / 100; // Set volume based on globalVolume (0-100 to 0-1)
        timerAlertSound.play().catch(error => {
          console.error('Error playing timer alert sound:', error);
        });
        setTimerAlertPlayed(true);
      } catch (error) {
        console.error('Error creating timer alert sound:', error);
      }
    };


    // Set up the interval for the timer
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        // When timer reaches 10 seconds
        if (prev === 10) {
          // Play timer alert sound
          playTimerAlert();
        }

        // When timer reaches 0
        if (prev <= 1) {
          return handleTimeExpiration();
        }

        // Normal countdown
        return prev - 1;
      });
    }, 1000);

    // Cleanup function
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [loading, showResults, mapData, showMapModal, sounds, handleTimeExpiration, selectedTimerAlert, disableSoundAlerts, globalMuted, globalVolume]);

  // Handle zoom on wheel event
  const handleWheel = (e) => {
    e.preventDefault();

    // Determine zoom direction
    const zoomDirection = e.deltaY < 0 ? 1 : -1;

    // Calculate new zoom level with limits
    const zoomFactor = 0.1;
    const newZoomLevel = Math.max(1, Math.min(5, zoomLevel + (zoomDirection * zoomFactor)));

    // Only update if zoom level changed
    if (newZoomLevel !== zoomLevel) {
      setZoomLevel(newZoomLevel);
    }
  };

  // Handle mouse down for panning
  const handleMouseDown = (e) => {
    // Only enable dragging if zoomed in
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  // Handle mouse move for panning
  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      // Calculate the distance moved
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;

      // Update drag start position
      setDragStart({ x: e.clientX, y: e.clientY });

      // Update pan position with limits
      setPanPosition(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
    }
  };

  // Handle mouse up to stop panning
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Handle map selection
  const handleMapSelect = (mapId) => {
    setTempSelectedMap(mapId);
    setSelectedPosition(null); // Reset position when a new map is selected
    resetZoomAndPan(); // Reset zoom and pan when selecting a new map
    setShowMapModal(true);
  };

  // Handle modal cancellation
  const handleModalCancel = () => {
    setShowMapModal(false);
    setTempSelectedMap('');
    resetZoomAndPan();
  };

  // Handle modal confirmation
  const handleModalConfirm = () => {
    // Only proceed if a map is selected
    if (!tempSelectedMap) return;

    // Get or create a relative position
    let relativePositionToUse = selectedPositionRelative;

    // If no position is selected, use the center of the map
    if (!relativePositionToUse) {
      relativePositionToUse = { x: 0.5, y: 0.5 };
      setSelectedPositionRelative(relativePositionToUse);
    }

    // Always calculate the absolute position based on the current container dimensions
    if (modalMapContainerRef.current && modalMapImageRef.current) {
      // Get container dimensions
      const containerRect = modalMapContainerRef.current.getBoundingClientRect();

      // Get map dimensions
      const mapWidth = mapData[tempSelectedMap]?.width || 100;
      const mapHeight = mapData[tempSelectedMap]?.height || 100;

      // Calculate aspect ratios
      const imageAspectRatio = mapWidth / mapHeight;
      const containerAspectRatio = containerRect.width / containerRect.height;

      // Calculate actual image dimensions and offsets
      let actualImageWidth, actualImageHeight;
      let offsetX = 0, offsetY = 0;

      if (imageAspectRatio > containerAspectRatio) {
        // Image is wider than container (letterboxed)
        actualImageWidth = containerRect.width;
        actualImageHeight = containerRect.width / imageAspectRatio;
        offsetY = (containerRect.height - actualImageHeight) / 2;
      } else {
        // Image is taller than container (pillarboxed)
        actualImageHeight = containerRect.height;
        actualImageWidth = containerRect.height * imageAspectRatio;
        offsetX = (containerRect.width - actualImageWidth) / 2;
      }

      // Calculate absolute position
      const absolutePosition = {
        x: offsetX + (relativePositionToUse.x * actualImageWidth),
        y: offsetY + (relativePositionToUse.y * actualImageHeight)
      };

      // Update the state with the calculated absolute position
      setSelectedPosition(absolutePosition);
    }

    // Reset zoom and pan
    resetZoomAndPan();

    // Update the state for UI consistency
    setSelectedMap(tempSelectedMap);
    setShowMapModal(false);

    // Stop timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Prepare the position for submission
    // Get map dimensions from mapData
    const mapWidth = mapData[tempSelectedMap]?.width || 100;
    const mapHeight = mapData[tempSelectedMap]?.height || 100;

    // Convert relative coordinates (0-1 range) to absolute pixel coordinates
    const positionToSubmit = {
      x: relativePositionToUse.x * mapWidth,
      y: relativePositionToUse.y * mapHeight
    };

    // Submit the position directly to the API
    submitPosition(
      gameState.challengeId,
      tempSelectedMap,
      positionToSubmit,
      gameState.sessionId
    ).then(result => {
      // Update scores in game state
      const updatedScores = [...gameState.scores, result.score];
      updateGameState({ scores: updatedScores });

      // Set round result and show results
      setRoundResult(result);
      setShowResults(true);
    }).catch(error => {
      console.error('Error submitting position:', error);
      showError(t('game.errorSubmittingPosition', 'Error submitting position. Please try again.'));
    });
  };

  // Handle position selection on map
  const handleMapClick = (e) => {
    // Determine if we're in the modal or results view
    const isModal = showMapModal;
    const containerRef = isModal ? modalMapContainerRef : selectedMapContainerRef;
    const imageRef = isModal ? modalMapImageRef : selectedMapImageRef;
    const mapId = isModal ? tempSelectedMap : selectedMap;

    // Only proceed if we have valid refs and a selected map
    if (!containerRef.current || !imageRef.current || !mapId) return;

    // Get container dimensions
    const containerRect = containerRef.current.getBoundingClientRect();

    // Get map dimensions from mapData
    const mapWidth = mapData[mapId]?.width || 100;
    const mapHeight = mapData[mapId]?.height || 100;

    // Calculate aspect ratios
    const imageAspectRatio = mapWidth / mapHeight;
    const containerAspectRatio = containerRect.width / containerRect.height;

    // Calculate actual image dimensions and offsets within the container
    let actualImageWidth, actualImageHeight;
    let offsetX = 0, offsetY = 0;

    if (imageAspectRatio > containerAspectRatio) {
      // Image is wider than container (letterboxed - empty space on top and bottom)
      actualImageWidth = containerRect.width;
      actualImageHeight = containerRect.width / imageAspectRatio;
      offsetY = (containerRect.height - actualImageHeight) / 2;
    } else {
      // Image is taller than container (pillarboxed - empty space on sides)
      actualImageHeight = containerRect.height;
      actualImageWidth = containerRect.height * imageAspectRatio;
      offsetX = (containerRect.width - actualImageWidth) / 2;
    }

    // Calculate click position relative to the container
    const clickX = e.clientX - containerRect.left;
    const clickY = e.clientY - containerRect.top;

    // Check if click is within the actual image bounds
    if (
      clickX >= offsetX && 
      clickX <= offsetX + actualImageWidth &&
      clickY >= offsetY && 
      clickY <= offsetY + actualImageHeight
    ) {
      // Adjust for zoom and pan
      // First, calculate the center of the container
      const centerX = containerRect.width / 2;
      const centerY = containerRect.height / 2;

      // Calculate the click position relative to the center
      const relativeToCenter = {
        x: clickX - centerX,
        y: clickY - centerY
      };

      // Adjust for zoom and pan
      const adjustedX = (relativeToCenter.x / zoomLevel) - (panPosition.x / zoomLevel) + centerX;
      const adjustedY = (relativeToCenter.y / zoomLevel) - (panPosition.y / zoomLevel) + centerY;

      // Calculate position relative to the actual image (removing offsets)
      const imageX = adjustedX - offsetX;
      const imageY = adjustedY - offsetY;

      // Calculate relative position (0-1 range)
      const relativePos = {
        x: imageX / actualImageWidth,
        y: imageY / actualImageHeight
      };

      // Ensure values are within 0-1 range
      const clampedRelativePos = {
        x: Math.max(0, Math.min(1, relativePos.x)),
        y: Math.max(0, Math.min(1, relativePos.y))
      };

      // Store the relative position
      setSelectedPositionRelative(clampedRelativePos);

      // Calculate the absolute position based on the relative position
      const absolutePos = relativeToAbsolutePosition(clampedRelativePos, containerRef, imageRef);
      setSelectedPosition(absolutePos);
    }
  };

  // Handle submit button click
  // This function is defined but not currently used in the component
  // Keeping it commented out for potential future use
  /*
  const handleSubmit = useCallback(async (mapOverride = null) => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Check if mapData is empty
    if (Object.keys(mapData).length === 0) {
      console.error('No map data available');
      // Try to load map data again
      try {
        const maps = await fetchMapData();
        setMapData(maps);

        // Check if we successfully loaded map data
        if (Object.keys(maps).length === 0) {
          console.error('Failed to load map data for submission');
          // Show an error message or return to main screen
          showError(t('game.errorLoadingMapData', 'Error loading map data. Please try again.'));
          return; // Don't proceed if no map data
        }
      } catch (error) {
        console.error('Error loading map data:', error);
        showError(t('game.errorLoadingMapData', 'Error loading map data. Please try again.'));
        return; // Don't proceed if error loading map data
      }
    }

    // Prepare the map and position values to use in the API call
    // Use overrides if provided, otherwise use state values
    let mapToSubmit = mapOverride !== null ? mapOverride : selectedMap;
    let positionToSubmit;
    let relativePositionToUse;

    if (selectedPositionRelative) {
      // If we have a relative position, convert it to absolute pixel coordinates for API
      // Get map dimensions from mapData
      const mapWidth = mapData[mapToSubmit]?.width || 100;
      const mapHeight = mapData[mapToSubmit]?.height || 100;

      // Convert relative coordinates (0-1 range) to absolute pixel coordinates
      positionToSubmit = {
        x: selectedPositionRelative.x * mapWidth,
        y: selectedPositionRelative.y * mapHeight
      };
      relativePositionToUse = selectedPositionRelative;
    } else if (selectedPosition) {
      // Fallback case: we have an absolute position but no relative position
      // Calculate a relative position from the absolute position
      const containerRef = selectedMapContainerRef.current || modalMapContainerRef.current;

      if (containerRef) {
        const containerRect = containerRef.getBoundingClientRect();
        const mapWidth = mapData[mapToSubmit]?.width || 100;
        const mapHeight = mapData[mapToSubmit]?.height || 100;

        // Calculate aspect ratios
        const imageAspectRatio = mapWidth / mapHeight;
        const containerAspectRatio = containerRect.width / containerRect.height;

        // Calculate actual image dimensions and offsets
        let actualImageWidth, actualImageHeight;
        let offsetX = 0, offsetY = 0;

        if (imageAspectRatio > containerAspectRatio) {
          // Image is wider than container (letterboxed)
          actualImageWidth = containerRect.width;
          actualImageHeight = containerRect.width / imageAspectRatio;
          offsetY = (containerRect.height - actualImageHeight) / 2;
        } else {
          // Image is taller than container (pillarboxed)
          actualImageHeight = containerRect.height;
          actualImageWidth = containerRect.height * imageAspectRatio;
          offsetX = (containerRect.width - actualImageWidth) / 2;
        }

        // Calculate relative position from absolute position
        const relativePos = {
          x: (selectedPosition.x - offsetX) / actualImageWidth,
          y: (selectedPosition.y - offsetY) / actualImageHeight
        };

        // Ensure values are within 0-1 range
        relativePos.x = Math.max(0, Math.min(1, relativePos.x));
        relativePos.y = Math.max(0, Math.min(1, relativePos.y));

        // Store the calculated relative position
        setSelectedPositionRelative(relativePos);
        relativePositionToUse = relativePos;

        // Convert to absolute pixel coordinates for API
        // Reuse the mapWidth and mapHeight variables declared above
        // No need to redeclare them

        positionToSubmit = {
          x: relativePos.x * mapWidth,
          y: relativePos.y * mapHeight
        };
      } else {
        // If we can't calculate a relative position, use a default position
        const defaultRelativePos = { x: 0.5, y: 0.5 }; // Center in 0-1 range

        // Get map dimensions from mapData
        const mapWidth = mapData[mapToSubmit]?.width || 100;
        const mapHeight = mapData[mapToSubmit]?.height || 100;

        // Convert relative coordinates (0-1 range) to absolute pixel coordinates
        positionToSubmit = { 
          x: defaultRelativePos.x * mapWidth, 
          y: defaultRelativePos.y * mapHeight 
        };
        console.warn('Using default position (no container ref)');
      }
    } else {
      // If no selection, use a default position
      mapToSubmit = Object.keys(mapData)[0] || '';
      relativePositionToUse = { x: 0.5, y: 0.5 }; // Center in 0-1 range

      // Get map dimensions from mapData
      const mapWidth = mapData[mapToSubmit]?.width || 100;
      const mapHeight = mapData[mapToSubmit]?.height || 100;

      // Convert relative coordinates (0-1 range) to absolute pixel coordinates
      positionToSubmit = { 
        x: relativePositionToUse.x * mapWidth, 
        y: relativePositionToUse.y * mapHeight 
      };

      // Also update the state for UI consistency
      setSelectedMap(mapToSubmit);
      setSelectedPosition({ 
        x: relativePositionToUse.x * 100, 
        y: relativePositionToUse.y * 100 
      });
      setSelectedPositionRelative(relativePositionToUse);
    }

    try {
      // Make sure we're sending valid values to the API
      if (!gameState.challengeId || !mapToSubmit || !positionToSubmit || !positionToSubmit.x || !positionToSubmit.y) {
        console.error('Invalid data for submission:', { 
          gameId: gameState.challengeId, 
          map: mapToSubmit, 
          position: positionToSubmit 
        });
        showError(t('game.errorSubmittingPosition', 'Error submitting position. Missing required data.'));
        return;
      }

      const result = await submitPosition(
        gameState.challengeId,
        mapToSubmit,
        positionToSubmit,
        gameState.sessionId
      );

      // Check if time expired
      if (result.timeExpired) {
        // Create a result object with 0 points
        const timeExpiredResult = {
          score: 0,
          distance: -1, // -1 to hide the line and actual position
          timeExpired: true
        };

        // Update scores in game state with 0 points
        const updatedScores = [...gameState.scores, 0];
        updateGameState({ scores: updatedScores });

        // Set time expired flag
        setTimeExpired(true);

        // Set round result and show results
        setRoundResult(timeExpiredResult);
        setShowResults(true);
        return;
      }

      // Update scores in game state
      const updatedScores = [...gameState.scores, result.score];
      updateGameState({ scores: updatedScores });

      // Set round result and show results in one render cycle
      setRoundResult(result);
      setShowResults(true);
    } catch (error) {
      console.error('Error submitting position:', error);
      showError(t('game.errorSubmittingPosition', 'Error submitting position. Please try again.'));
    }
  }, [
    timerRef, 
    mapData, 
    selectedMap, 
    selectedPosition, 
    selectedPositionRelative, 
    selectedMapContainerRef, 
    modalMapContainerRef, 
    gameState, 
    t, 
    setMapData, 
    setSelectedMap, 
    setSelectedPosition, 
    setSelectedPositionRelative, 
    setRoundResult, 
    setShowResults, 
    setTimeExpired, 
    updateGameState
  ]);
  */

  // Handle next round button click
  const handleNextRound = async () => {
    // Clear challenge cache to ensure a fresh challenge is loaded for the next round
    clearApiCache('challenge');

    setShowResults(false);
    setSelectedMap('');
    setSelectedPosition(null);
    setSelectedPositionRelative(null);
    setRoundResult(null);
    setTimeRemaining(60);
    setTimeExpired(false);

    // Reset zoom and pan
    resetZoomAndPan();

    // Reset challengeLoaded flag to ensure a new challenge is loaded for the next round
    setChallengeLoaded(false);

    // Reset challengeId to ensure a new challenge is loaded for the next round
    updateGameState({ challengeId: null });

    // Check if map data is available
    if (Object.keys(mapData).length === 0) {
      console.warn('No map data available for next round, attempting to load map data');
      // Try to load map data again
      try {
        const maps = await fetchMapData();
        setMapData(maps);

        // Only proceed if we successfully loaded map data
        if (Object.keys(maps).length === 0) {
          console.error('Failed to load map data for next round');
          onGameEnd();
          return;
        }
      } catch (error) {
        console.error('Error loading map data for next round:', error);
        // If we can't load map data, return to main screen
        handleGameEnd();
        return;
      }
    }

    // Only proceed to next round or finalize session if we have map data
    if (gameState.currentRound >= 5) {
      // Game is over, finalize session
      finalizeSession();
    } else {
      // Start next round
      updateGameState({ currentRound: gameState.currentRound + 1 });
    }
  };

  // Finalize game session
  const finalizeSession = async () => {
    try {

      const finalResult = await finalizeGameSession(gameState.sessionId);
      // Show final results
      setRoundResult({
        ...finalResult,
        isFinal: true
      });

      // Show the final score modal
      setShowFinalScoreModal(true);
    } catch (error) {
      console.error('Error finalizing session:', error);
      // Even if there's an error, we should still end the game
      handleGameEnd();
    }
  };

  // Format time remaining
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Show error modal with a message and optional callback
  const showError = (message, callback = null) => {
    setErrorMessage(message);
    setErrorCallback(() => callback);
    setShowErrorModal(true);
  };

  // Handle error modal close
  const handleErrorModalClose = () => {
    setShowErrorModal(false);
    if (errorCallback) {
      errorCallback();
      setErrorCallback(null);
    }
  };

  // State for volume dropdown visibility
  const [showVolumeControl, setShowVolumeControl] = useState(false);

  // Toggle session mute state
  const toggleSessionMute = () => {
    if (globalVolume === 0) {
      // If volume is 0, set it to 100 when unmuting
      setGlobalVolume(100);
      setGlobalMuted(false);
    } else {
      // Toggle mute state
      setGlobalMuted(prev => !prev);
    }
  };

  // Handle volume change
  const handleVolumeChange = (e) => {
    const newVolume = parseInt(e.target.value, 10);
    setGlobalVolume(newVolume);

    // If volume is set to 0, mute the audio
    // If volume is changed from 0, unmute the audio
    if (newVolume === 0) {
      setGlobalMuted(true);
    } else if (globalMuted && newVolume > 0) {
      setGlobalMuted(false);
    }
  };

  // Toggle volume control visibility
  const toggleVolumeControl = (e) => {
    e.stopPropagation(); // Prevent the click from triggering the mute toggle
    setShowVolumeControl(prev => !prev);
  };

  // Close volume control when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Check if the click is outside the volume control container
      const volumeContainer = document.querySelector('.volume-control-container');
      if (showVolumeControl && volumeContainer && !volumeContainer.contains(e.target)) {
        setShowVolumeControl(false);
      }
    };

    // Add event listener when dropdown is shown
    if (showVolumeControl) {
      document.addEventListener('click', handleClickOutside);
    }

    // Clean up the event listener
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showVolumeControl]);

  return (
    <div className="game-screen">
      <div className="game-header">
        <div className="round-info">
          <span className="round-label">{t('game.round')}</span>
          <span className="round-number">{gameState.currentRound}</span>
          <span className="round-total">/ 5</span>
        </div>

        <div className="timer">
          <i className="fas fa-clock"></i>
          <span className="time-remaining">{formatTime(timeRemaining)}</span>
        </div>

        <div className="score-display">
          <span className="score-label">{t('game.score')}</span>
          <span className="score-value">
            {gameState.scores.reduce((sum, score) => sum + score, 0)}
          </span>
        </div>
      </div>

      <div className={`game-content ${showResults ? 'results-shown' : ''} ${showResults && roundResult?.isFinal ? 'final-state' : ''}`}>
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>{t('game.loading')}</p>
          </div>
        ) : (
          <>
            {showResults && (
              <div className="results-container top-results">
                <div className="result-details">
                  <h3>{t('game.roundResult')}</h3>
                  <div className="result-stats">
                    <div className="result-stat">
                      <span className="stat-label">{t('game.selectedMap')}</span>
                      <span className="stat-value">{selectedMap.charAt(0).toUpperCase() + selectedMap.slice(1)}</span>
                    </div>
                    <div className="result-stat">
                      <span className="stat-label">{t('game.score')}</span>
                      <span className="stat-value">{roundResult?.score || 0}</span>
                    </div>
                    <div className="result-stat">
                      <span className="stat-label">{t('game.distance')}</span>
                      <span className="stat-value">
                        {roundResult?.distance !== -1 ? `${roundResult?.distance || 0} ${t('game.pixels')}` : ''}
                      </span>
                    </div>
                    <div className="result-stat">
                      <span className="stat-label">{t('game.currentRound')}</span>
                      <span className="stat-value">{gameState.currentRound} / 5</span>
                    </div>
                    <div className="result-stat">
                      <span className="stat-label">{t('game.totalScore')}</span>
                      <span className="stat-value">
                        {gameState.scores.reduce((sum, score) => sum + score, 0)}
                      </span>
                    </div>
                  </div>
                  <p className="location-description">
                    {timeExpired 
                      ? t('game.timeExpiredScore', 'Time expired. Your score for this round is 0 points.')
                      : (roundResult?.actualPosition?.description || t('game.unknownLocation'))}
                  </p>
                  {!roundResult?.isFinal && (
                    <button 
                      className="btn btn-primary next-round-btn"
                      onClick={handleNextRound}
                    >
                      {t('game.nextRoundButton')}
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="challenge-container">
              {!showResults && (
                <div className="challenge-image-container">
                  <img 
                    src={gameState.challengeImage} 
                    alt={t('game.challengeImage')} 
                    className="challenge-image" 
                  />
                </div>
              )}

              <div className="map-selection">
                {!selectedMap || (selectedMap && !showResults) || (showResults && timeExpired) ? (
                  <>
                    <h3>{t('game.selectMap')}</h3>
                    <div className="map-buttons">
                      {Object.keys(mapData).map(mapId => (
                        <button
                          key={mapId}
                          className={`map-button ${selectedMap === mapId ? 'selected' : ''}`}
                          onClick={() => handleMapSelect(mapId)}
                          disabled={showResults && timeExpired}
                        >
                          {mapId.charAt(0).toUpperCase() + mapId.slice(1)}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="selected-map-result">
                    <div 
                      className="selected-map-container" 
                      ref={selectedMapContainerRef} 
                      onClick={!showResults ? handleMapClick : undefined}
                      onWheel={handleWheel}
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseUp}
                      onContextMenu={(e) => e.preventDefault()}
                      style={{ cursor: zoomLevel > 1 ? 'grab' : 'crosshair' }}
                    >
                      <div className="zoom-controls">
                        <button onClick={(e) => {
                          e.stopPropagation();
                          setZoomLevel(prev => Math.min(5, prev + 0.1));
                        }}>+</button>
                        <button onClick={(e) => {
                          e.stopPropagation();
                          setZoomLevel(prev => Math.max(1, prev - 0.1));
                        }}>-</button>
                        <button onClick={(e) => {
                          e.stopPropagation();
                          resetZoomAndPan();
                        }}>Reset</button>
                      </div>
                      <img 
                        src={`/images/maps/${selectedMap}.png`}
                        alt={selectedMap.charAt(0).toUpperCase() + selectedMap.slice(1)} 
                        className="selected-map-image"
                        ref={selectedMapImageRef}
                        style={{
                          transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                          transformOrigin: 'center',
                          transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                        }}
                      />
                      <div 
                        className="map-overlay" 
                        ref={selectedMapOverlayRef}
                        style={{
                          transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                          transformOrigin: 'center',
                          transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                        }}
                      >
                        {selectedPosition && selectedPositionRelative && (
                          <div 
                            className="position-marker small"
                            style={{
                              position: 'absolute',
                              left: `${selectedPosition.x}px`,
                              top: `${selectedPosition.y}px`,
                              zIndex: '10',
                              pointerEvents: 'none',
                              transform: 'translate(-50%, -50%)'
                            }}
                          ></div>
                        )}
                        {/* Display actual position as blue point if user selected the correct map */}
                        {showResults && roundResult && roundResult.distance !== -1 && roundResult.actualPosition && (
                          <>
                            {/* Draw line between user's guess and actual position */}
                            {selectedPosition && selectedPositionRelative && (
                              <svg 
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  zIndex: '9',
                                  pointerEvents: 'none'
                                }}
                              >
                                <line 
                                  x1={selectedPosition.x}
                                  y1={selectedPosition.y}
                                  x2={relativeToAbsolutePosition({x: roundResult.actualPosition.x / mapData[selectedMap]?.width, y: roundResult.actualPosition.y / mapData[selectedMap]?.height}, selectedMapContainerRef, selectedMapImageRef)?.x}
                                  y2={relativeToAbsolutePosition({x: roundResult.actualPosition.x / mapData[selectedMap]?.width, y: roundResult.actualPosition.y / mapData[selectedMap]?.height}, selectedMapContainerRef, selectedMapImageRef)?.y}
                                  style={{
                                    stroke: '#ffffff',
                                    strokeWidth: '2px',
                                    strokeDasharray: '5,5'
                                  }}
                                />
                              </svg>
                            )}
                            <div 
                              className="position-marker small actual"
                              style={{
                                position: 'absolute',
                                left: `${relativeToAbsolutePosition({x: roundResult.actualPosition.x / mapData[selectedMap]?.width, y: roundResult.actualPosition.y / mapData[selectedMap]?.height}, selectedMapContainerRef, selectedMapImageRef)?.x}px`,
                                top: `${relativeToAbsolutePosition({x: roundResult.actualPosition.x / mapData[selectedMap]?.width, y: roundResult.actualPosition.y / mapData[selectedMap]?.height}, selectedMapContainerRef, selectedMapImageRef)?.y}px`,
                                zIndex: '11',
                                pointerEvents: 'none',
                                transform: 'translate(-50%, -50%)'
                              }}
                            ></div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="game-controls">
        {showResults && roundResult?.isFinal && !showFinalScoreModal && (
          <button 
            className="btn btn-primary play-again-btn"
            onClick={handleGameEnd}
          >
            {t('game.playAgainButton')}
          </button>
        )}
      </div>

      {/* Map Selection Modal */}
      {showMapModal && (
        <div className="map-modal-overlay">
          <div className="map-modal">
            <div className="map-modal-header">
              <h3>
                <span>{t('game.selectPosition')}</span>
                <span className="time-in-title">{formatTime(timeRemaining)}</span>
              </h3>
              <button className="modal-close-btn" onClick={handleModalCancel}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="map-modal-content">
              <div 
                className="map-container" 
                ref={modalMapContainerRef} 
                onClick={handleMapClick}
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onContextMenu={(e) => e.preventDefault()}
                style={{ cursor: zoomLevel > 1 ? 'grab' : 'crosshair' }}
              >
                <div className="zoom-controls">
                  <button onClick={(e) => {
                    e.stopPropagation();
                    setZoomLevel(prev => Math.min(5, prev + 0.1));
                  }}>+</button>
                  <button onClick={(e) => {
                    e.stopPropagation();
                    setZoomLevel(prev => Math.max(1, prev - 0.1));
                  }}>-</button>
                  <button onClick={(e) => {
                    e.stopPropagation();
                    resetZoomAndPan();
                  }}>Reset</button>
                </div>
                <img 
                  src={`/images/maps/${tempSelectedMap}.png`}
                  alt={tempSelectedMap.charAt(0).toUpperCase() + tempSelectedMap.slice(1)} 
                  className="map-image"
                  ref={modalMapImageRef}
                  style={{
                    transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                    transformOrigin: 'center',
                    transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                  }}
                />
                <div 
                  className="map-overlay" 
                  ref={modalMapOverlayRef}
                  style={{
                    transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                    transformOrigin: 'center',
                    transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                  }}
                >
                  {selectedPosition && selectedPositionRelative && (
                    <div 
                      className="position-marker"
                      style={{
                        position: 'absolute',
                        left: `${selectedPosition.x}px`,
                        top: `${selectedPosition.y}px`,
                        zIndex: '10',
                        pointerEvents: 'none',
                        transform: 'translate(-50%, -50%)'
                      }}
                    ></div>
                  )}
                </div>
              </div>
            </div>
            <div className="map-modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={handleModalCancel}
              >
                {t('game.cancel')}
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleModalConfirm}
              >
                {t('game.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="map-modal-overlay">
          <div className="map-modal">
            <div className="map-modal-header">
              <h3>{t('game.error', 'Error')}</h3>
              <button className="modal-close-btn" onClick={handleErrorModalClose}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="map-modal-content">
              <p className="error-message">{errorMessage}</p>
            </div>
            <div className="map-modal-footer">
              <button 
                className="btn btn-primary"
                onClick={handleErrorModalClose}
              >
                {t('game.ok', 'OK')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Final Score Modal */}
      {showFinalScoreModal && (
        <div className="map-modal-overlay">
          <div className="map-modal final-score-modal">
            <div className="map-modal-header">
              <h3>{t('game.finalScore', 'Final Score')}</h3>
              <button className="modal-close-btn" onClick={handleGameEnd}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="map-modal-content">
              <div className="final-score-container">
                <div className="final-score">
                  <h4>{t('game.totalScore', 'Total Score')}</h4>
                  <span className="final-score-value">
                    {gameState.scores.reduce((sum, score) => sum + score, 0)}
                  </span>
                </div>

                <div className="rounds-scores">
                  <h4>{t('game.roundScores', 'Round Scores')}</h4>
                  {gameState.scores.map((score, index) => (
                    <div key={index} className="round-score-item">
                      <div className="round-info">
                        <span className="round-label">{t('game.round')} {index + 1}</span>
                        <span className="round-score">{score}</span>
                      </div>
                      <div className="score-progress-container">
                        <div 
                          className="score-progress-bar" 
                          style={{
                            width: `${Math.min(100, (score / 5000) * 100)}%`,
                            backgroundColor: `hsl(${Math.min(120, (score / 5000) * 120)}, 100%, 45%)`
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="map-modal-footer">
              <button 
                className="btn btn-primary"
                onClick={handleGameEnd}
              >
                {t('game.playAgain', 'Play Again')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating mute button with volume control */}
      <div className="volume-control-container">
        <button 
          className={`floating-mute-button ${globalMuted ? 'muted' : ''}`}
          onClick={toggleSessionMute}
          title={globalMuted ? t('game.unmute', 'Unmute for this session') : t('game.mute', 'Mute for this session')}
        >
          <i className={`fas ${globalMuted ? 'fa-volume-mute' : globalVolume > 66 ? 'fa-volume-up' : globalVolume > 33 ? 'fa-volume-down' : 'fa-volume-off'}`}></i>
          <i className="fas fa-caret-down volume-caret" onClick={toggleVolumeControl}></i>
        </button>

        {showVolumeControl && (
          <div className="volume-dropdown">
            <div className="volume-title">{t('game.volumeControl', 'Volume Control')}</div>
            <input 
              type="range" 
              min="0" 
              max="100" 
              value={globalVolume} 
              onChange={handleVolumeChange}
              className="volume-slider"
              title={t('game.volumeLevel', 'Volume Level')}
            />
            <div className="volume-value">{globalVolume}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameScreen;
